import React, { useEffect, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useAPI } from '../../../Contexts/APIContext'
import { useAuth } from '../../../Contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useInView } from 'react-intersection-observer'
import { useSongsStore } from '../../../Storage/songsPage/useSongsStore'
import { isEmailInSongManagementTeam } from '../../../Helpers/RestrictedEmailsArray'
import DebounceSearch from '../../../Components/GeneralComponents/DebounceSearch/DebounceSearch'
import OptionsList from '../../../Components/GeneralComponents/OptionsList/OptionsList'
import SongGridItem from '../../../Components/DashboardComponents/SongGridItem/SongGridItem'
import SongListItem from '../../../Components/DashboardComponents/SongListItem/SongListItem'
import Loader from '../../../Components/GeneralComponents/Loader'
import classes from './index.module.css'
import useAdminAccess from '../../../services/hooks/useAdminAccess'

export default function AllSongs() {
  const pageLimit = 20
  const { isInternal } = useAdminAccess()
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('songsViewMode') ?? 'grid')

  function handleSetViewMode(mode) {
    setViewMode(mode)
    localStorage.setItem('songsViewMode', mode)
  }
  const { getCurrentUser, logout } = useAuth()
  const { getAllSongs, getSearchAllSongs } = useAPI()
  const navigate = useNavigate()
  const [ref, inView] = useInView()
  const [refSearch, inViewSearch] = useInView()

  const searchTerm = useSongsStore(state => state.searchTerm)
  const setSearchTerm = useSongsStore(state => state.setSearchTerm)

  const inputValue = useSongsStore(state => state.inputValue)
  const setInputValue = useSongsStore(state => state.setInputValue)

  const options = useSongsStore(state => state.options)
  const selectedOption = useSongsStore(state => state.selectedOption)
  const setSelectedOption = useSongsStore(state => state.setSelectedOption)

  const syncOptions = useSongsStore(state => state.syncOptions)
  const selectedSyncOption = useSongsStore(state => state.selectedSyncOption)
  const setSelectedSyncOption = useSongsStore(state => state.setSelectedSyncOption)

  const { data, isError, error, isLoading, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['AllSongs', searchTerm, selectedOption.id, selectedSyncOption.id],
      queryFn: ({ pageParam = 1 }) =>
        searchTerm === ''
          ? getAllSongs(pageParam, pageLimit, selectedOption.id, selectedSyncOption.id)
          : getSearchAllSongs(searchTerm, pageParam, pageLimit),
      getNextPageParam: pageData =>
        pageData.p * pageLimit < pageData.songs_count ? pageData.p + 1 : null,
    })

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  useEffect(() => {
    if (inView) {
      fetchNextPage()
    }
  }, [inView])

  function handleTableScroll(e) {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop - clientHeight < 300 && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  function handleClickOnSong(song) {
    let currentUser = getCurrentUser()
    if (currentUser == null || currentUser == undefined) {
      logout()
    } else {
      if (isEmailInSongManagementTeam(currentUser?.email)) {
        navigate('/edit-song/' + song.f_id)
      } else {
        navigate('/song/' + song.f_id)
      }
    }
  }

  return (
    <div className={`${classes.wrapper} ${viewMode === 'list' ? classes.wrapperList : ''}`}>
      <div className={`${classes.header} ${viewMode === 'list' ? classes.headerList : ''}`}>
        <h1 className={classes.title}>Songs</h1>
        <div className={classes.searchSortContainer}>
          {!searchTerm && (
            <OptionsList
              options={syncOptions}
              selectedOption={selectedSyncOption}
              setSelectedOption={setSelectedSyncOption}
            />
          )}
          {!searchTerm && (
            <OptionsList
              options={options}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
            />
          )}
          <DebounceSearch
            inputValue={inputValue}
            setInputValue={setInputValue}
            searchValue={searchTerm}
            setSearchValue={setSearchTerm}
            placeholder={'Search for Songs, Artists...'}
          />
          {isInternal && (
            <div className={classes.viewToggle}>
              <button
                className={`${classes.viewBtn} ${viewMode === 'grid' ? classes.viewBtnActive : ''}`}
                onClick={() => handleSetViewMode('grid')}
                title="Grid view"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="0" y="0" width="7" height="7" rx="1" />
                  <rect x="9" y="0" width="7" height="7" rx="1" />
                  <rect x="0" y="9" width="7" height="7" rx="1" />
                  <rect x="9" y="9" width="7" height="7" rx="1" />
                </svg>
              </button>
              <button
                className={`${classes.viewBtn} ${viewMode === 'list' ? classes.viewBtnActive : ''}`}
                onClick={() => handleSetViewMode('list')}
                title="List view"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="0" y="1" width="16" height="2" rx="1" />
                  <rect x="0" y="7" width="16" height="2" rx="1" />
                  <rect x="0" y="13" width="16" height="2" rx="1" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {viewMode === 'grid' && (
        <div className={classes.songs}>
          {!isError &&
            data &&
            data.pages.map((page, index) => (
              <React.Fragment key={index}>
                {page.songs.map(song => (
                  <div key={song.f_id} ref={ref}>
                    <SongGridItem
                      song={song}
                      onClick={songItem => handleClickOnSong(songItem)}
                    />
                  </div>
                ))}
              </React.Fragment>
            ))}
        </div>
      )}

      {viewMode === 'list' && (
        <div className={classes.tableWrapper} onScroll={handleTableScroll}>
          <table className={classes.table}>
            <thead>
              <tr className={classes.tableHead}>
                <th className={classes.th}>Song</th>
                <th className={classes.th}>Artist</th>
                <th className={`${classes.th} ${classes.center}`}>Lyric Status</th>
                <th className={classes.th}>Published</th>
                <th className={classes.th}>Alt Song ID</th>
                <th className={classes.th}>Artists</th>
                <th className={classes.th}>ISRCs</th>
              </tr>
            </thead>
            <tbody>
              {!isError &&
                data &&
                data.pages.map((page, index) => (
                  <React.Fragment key={index}>
                    {page.songs.map(song => (
                      <SongListItem
                        key={song.f_id}
                        song={song}
                        onClick={songItem => handleClickOnSong(songItem)}
                      />
                    ))}
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        </div>
      )}

      <Loader isLoading={isLoading || isFetchingNextPage} />

      {isError && (
        <h1 className={classes.errorText}>{`Failed to fetch data with code: ${
          error?.status ? error.status : 'unknown'
        }`}</h1>
      )}
    </div>
  )
}
